<template>
  <div class="details-card-wrapper">
    <v-theme-provider :dark="dark">
      <v-container class="container-box">
        <v-row
          class="card-box-row"
          justify="space-around"
        >
          <v-hover v-slot="{ hover }">
            <router-link
              v-if="to"
              :to="to"
              class="card-link"
            >
              <v-card
                :elevation="hover ? 10 : 0"
                :class="{ 'on-hover': hover }"
                class="details-card clickable-card"
              >
                <div
                  class="d-flex justify-center align-center image-container"
                  :style="{ 'background': background }"
                >
                  <img
                    :src="imgUrl"
                    class="product-image"
                    alt="product image"
                  >
                </div>
                <v-card-text>
                  <v-col
                    v-if="title"
                    :cols="12"
                  >
                    <base-subtitle
                      v-if="subtitle"
                      :title="$t(subtitle)"
                      space="1"
                    />
                    <base-title
                      :title="$t(title)"
                      class="text-uppercase"
                      space="1"
                    />
                    <base-divider :color="color" />

                    <div
                      v-if="showText"
                      class="card-text"
                    >
                      {{ $t(desc) }}
                    </div>
                    <base-body
                      v-if="!showText && (text1 || $slots.default)"
                      :text="$t(desc)"
                      :max-height="maxHeight"
                      space="3"
                    >
                      <slot />
                    </base-body>
                  </v-col>
                </v-card-text>
              </v-card>
            </router-link>
            <v-card
              v-else
              :elevation="hover ? 10 : 0"
              :class="{ 'on-hover': hover }"
              class="details-card not-clickable"
              @click.stop="handleNotClickable"
            >
              <div
                class="d-flex justify-center align-center image-container"
                :style="{ 'background': background }"
              >
                <img
                  :src="imgUrl"
                  class="product-image"
                  alt="product image"
                >
              </div>
              <v-card-text>
                <v-col
                  v-if="title"
                  :cols="12"
                >
                  <base-subtitle
                    v-if="subtitle"
                    :title="$t(subtitle)"
                    space="1"
                  />
                  <base-title
                    :title="$t(title)"
                    class="text-uppercase"
                    space="1"
                  />
                  <base-divider :color="color" />

                  <div
                    v-if="showText"
                    class="card-text"
                  >
                    {{ $t(desc) }}
                  </div>
                  <base-body
                    v-if="!showText && (text1 || $slots.default)"
                    :text="$t(desc)"
                    :max-height="maxHeight"
                    space="3"
                  >
                    <slot />
                  </base-body>
                </v-col>
              </v-card-text>
            </v-card>
          </v-hover>
        </v-row>
      </v-container>
    </v-theme-provider>
  </div>
</template>

<script>
  // Mixins
  import Heading from '@/mixins/heading'

  export default {
    name: 'BaseDetailsCard',

    mixins: [Heading],

    props: {
      dark: Boolean,
      callout: String,
      color: {
        type: String,
        default: 'primary',
      },
      to: Object,
      icon: String,
      background: String,
      desc: String,
      title: String,
      subtitle: String,
      imgUrl: String,
      showText: {
        type: Boolean,
        default: false,
      },
      showDivTitle: {
        type: Boolean,
        default: false,
      },
    },

    methods: {
      handleNotClickable() {
        const message = this.$t('products.contact_business')
        const actionText = this.$t('contact.title')
        // 使用全局 snackbar
        if (this.$root.$showSnackbar) {
          this.$root.$showSnackbar({
            message: message,
            color: 'info',
            timeout: 3000,
            actionText: actionText,
            actionCallback: () => {
              // 点击按钮后：先跳转到 Home，然后滚动到底部
              this.navigateToHomeAndScroll()
            },
          })
        }
      },

      navigateToHomeAndScroll() {
        // 检查当前路由
        const isOnHomePage = this.$route.name === 'Home'

        // 滚动到底部的函数
        const scrollToBottom = () => {
          // 等待 DOM 更新
          this.$nextTick(() => {
            window.scrollTo({
              top: document.documentElement.scrollHeight + 500,
              behavior: 'smooth',
            })
          })
        }

        // 如果不在 Home 页面，先跳转
        if (!isOnHomePage) {
          this.$router.push({ name: 'Home' }).then(() => {
            scrollToBottom()
          }).catch(() => {
            // 如果路由跳转失败，仍然尝试滚动
            scrollToBottom()
          })
        } else {
          // 如果已经在 Home 页面，直接滚动到底部
          scrollToBottom()
        }
      },
    },
  }
</script>

<style lang="sass" scoped>
.details-card-wrapper
  height: 100%
  display: flex
  flex-direction: column

::v-deep .v-container
  height: 100%
  padding: 0

::v-deep .v-row
  height: 100%
  margin: 0
  align-items: stretch

::v-deep .v-col
  height: 100%

::v-deep .v-hover
  height: 100%
  display: flex
  flex-direction: column
  width: 100%

.card-link
  text-decoration: none
  display: flex
  flex-direction: column
  height: 100%
  width: 100%
  color: inherit

  &:hover
    text-decoration: none
    color: inherit

.card-box-row
  height: 100%

.details-card
  height: 100%
  display: flex
  flex-direction: column
  min-height: 100%
  width: 100%

  .v-card-text
    flex: 1
    display: flex
    flex-direction: column
    min-height: 0

  ::v-deep .v-col
    flex: 1
    display: flex !important
    flex-direction: column !important
    min-height: 0

    // 确保标题区域不伸缩
    > *:not(.card-text):not(.base-body)
      flex-shrink: 0
      flex-grow: 0

.v-card:not(.on-hover)
  margin-top: 3px

.container-box
  height: 100%

.details-card.not-clickable
  cursor: pointer
  transition: all 0.3s ease

  &:hover
    transform: translateY(-2px)

  &:active
    transform: translateY(0)

  .image-container
    cursor: pointer

.details-card.clickable-card
  cursor: pointer
  transition: all 0.3s ease

  &:hover
    transform: translateY(-2px)

.image-container
  min-height: 200px
  height: 200px
  width: 100%
  overflow: hidden
  padding: 16px
  flex-shrink: 0

.product-image
  max-width: 100%
  max-height: 100%
  width: auto
  height: auto
  object-fit: contain
  display: block

.card-title
  font-size: 1rem
  font-weight: 700
  line-height: 2rem
  height: 64px
  letter-spacing: 0
  font-family: "Work Sans", sans-serif !important
  flex-shrink: 0

.card-text
  margin-top: auto !important
  letter-spacing: 0
  width: 100%
  flex-shrink: 0
  flex-grow: 0
  &::-webkit-scrollbar
    width: 5px
    height: 5px
  &::-webkit-scrollbar-thumb
    background-color: #c4c8cf35
    border: none

// 确保 card-text 在 v-col 中能正确应用 margin-top: auto
::v-deep .v-col .card-text
  margin-top: auto !important

// Snackbar 不会影响布局，Vuetify 会自动处理定位
</style>
